'use client';

import type { ShopperProducts } from '@/scapi';
import { getCategoryHref } from '@/extensions/snk/lib/utils';
import { Button } from '@/shared-ui/atoms/button';
import { Link } from '@/shared-ui/atoms/link';
import { LinkColumn } from '@/shared-ui/molecules/link-column';
import { cn } from '@/lib/utils';

type MegaMenuCategoryLinkColumnProps = {
    title: string;
    categories: ShopperProducts.schemas['Category'][];
    shopAll?: { label: string; categoryId: string };
    onNavigate?: () => void;
    className?: string;
};

export type MegaMenuLinkItem = {
    id: string;
    label: string;
    to: string;
};

type MegaMenuLinkColumnProps = {
    title: string;
    items: MegaMenuLinkItem[];
    onNavigate?: () => void;
    className?: string;
};

function MegaMenuLinkListItem({ label, to, onNavigate }: { label: string; to: string; onNavigate?: () => void }) {
    return (
        <li className="min-w-0 w-full">
            <Button
                asChild
                variant="link"
                className="h-auto w-full max-w-full min-w-0 justify-start px-0 normal-case overflow-hidden text-ellipsis">
                <Link to={to} onClick={onNavigate} title={label}>
                    {label}
                </Link>
            </Button>
        </li>
    );
}

export function MegaMenuLinkColumn({ title, items, onNavigate, className }: MegaMenuLinkColumnProps) {
    if (items.length === 0) {
        return null;
    }

    return (
        <LinkColumn.ListColumn title={title} className={cn('h-full', className)}>
            {items.map((item) => (
                <MegaMenuLinkListItem key={item.id} label={item.label} to={item.to} onNavigate={onNavigate} />
            ))}
        </LinkColumn.ListColumn>
    );
}

export function MegaMenuCategoryLinkColumn({
    title,
    categories,
    shopAll,
    onNavigate,
    className,
}: MegaMenuCategoryLinkColumnProps) {
    if (categories.length === 0 && !shopAll) {
        return null;
    }

    return (
        <LinkColumn.ListColumn title={title} className={cn('h-full', className)}>
            {categories.map((category) => (
                <MegaMenuLinkListItem
                    key={category.id}
                    label={category.name ?? category.id}
                    to={getCategoryHref(category)}
                    onNavigate={onNavigate}
                />
            ))}
            {shopAll ? (
                <MegaMenuLinkListItem
                    label={shopAll.label}
                    to={getCategoryHref({ id: shopAll.categoryId })}
                    onNavigate={onNavigate}
                />
            ) : null}
        </LinkColumn.ListColumn>
    );
}
